/**
 * Which language the panel speaks.
 *
 * The rules differ from the agent console's, and the difference is the whole
 * reason this file exists rather than reusing `apps/web/src/i18n/locale.ts`.
 *
 * In the console the USER picks, and their explicit choice outranks everything.
 * Here there is no account and no REMEMBERED choice; there is a VISITOR who is
 * reading a page in some language, and a BRAND that has said which languages it
 * staffs. Answering in a language the brand cannot reply in is worse than
 * answering in a second-best one the visitor can read, so the brand's configured
 * set is a filter over every other signal, not a tiebreak after them.
 *
 * "No remembered choice" is not the same as "no choice", and that sentence used
 * to be read as if it were. The panel HAS a language switcher — `Panel.vue`
 * renders one for every staffed locale that is not the current one — and this
 * function did not model its result, so the reconciliation watcher in
 * `useWidget.ts` recomputed from the browser and the staffed set and put the
 * locale straight back. Measured on the stand (2026-09-09): the panel greeted a
 * visitor in Ukrainian, the switcher said `English`, and clicking it changed
 * nothing at all — twice, with three seconds between. `chosen` is that missing
 * input, and it is FIRST because a person who has just clicked a language has
 * said something no header or navigator setting can outrank.
 *
 * THE DEVICE IS A SOURCE, NOT A GUESS, AND ITS TAGS ARE NOT CLEAN.
 *
 * `navigator.languages` is the visitor's own ordered preference list, set in
 * their OS or their browser, and it is the only signal here that describes the
 * PERSON rather than the page. It is read as data, never repaired into a
 * different answer: a tag becomes a locale or it becomes `null`, and `null` means
 * "this entry said nothing", which lets the NEXT entry speak instead of jumping
 * to the fallback.
 *
 * That distinction is why {@link deviceLanguages} and the tag repair below exist.
 * Three things arrive from real devices that a strict reader drops on the floor:
 *   - `uk_UA` with an UNDERSCORE, which Android WebViews and Java-derived user
 *     agents produce. `normalizeLocale` splits on `-`, so `uk_UA` reduced to
 *     `uk_ua`, matched nothing, and a Ukrainian visitor on a brand that staffs
 *     `en` and `uk` was greeted in English. That is precisely the "silently fall
 *     back to English when a better match exists" this function must not do.
 *   - an ABSENT `languages`. lib.dom types it as always present; iOS in-app
 *     WebViews have shipped without it, and `for (const tag of undefined)`
 *     throws. This function runs during the widget's FIRST render, so that throw
 *     is a blank panel on a customer's page, not merely a wrong language.
 *   - junk entries — `*`, `x-default`, `und`, an empty string. They reduce to
 *     `null` and are skipped, which is already right and is now asserted.
 *
 * REGION IS NEVER CROSSED, AND TODAY THAT COSTS NOTHING. Matching is on the
 * PRIMARY LANGUAGE SUBTAG: `uk-UA`, `uk_UA` and `uk-Cyrl-UA` all match a staffed
 * `uk`, and `en-US` is answered from the `en` catalogue (written in `en-GB`
 * prose — see `LOCALE_BCP47`). That is sound ONLY because every locale this
 * product ships is a LANGUAGE-level catalogue and no two of them share a primary
 * subtag. The day `LOCALES` gains `pt-BR` and `pt-PT`, primary-subtag matching
 * becomes the bug — it hands a Brazilian the Portuguese of Portugal — and the
 * answer is a region-aware match HERE, not a wider fallback. `locale.test.ts`
 * pins that precondition with a test over `LOCALES` itself, so the day it stops
 * holding a test fails instead of a visitor reading the wrong dialect.
 */
import { WIDGET_DEFAULT_LOCALE, normalizeWidgetLocale, type WidgetLocale } from './locales';

export interface LocaleSources {
  /**
   * What the VISITOR clicked in the panel's own switcher, this session, in this
   * frame. Highest priority and deliberately not persisted: it lives in component
   * state and dies with the frame, for the same reason the prechat answers are
   * not cached — nothing a visitor typed or tapped into a support panel should
   * outlive the conversation it was for.
   *
   * Still filtered by the staffed set, and that costs nothing today because the
   * switcher is built FROM that set. It is filtered anyway: the day a deep link
   * or a postMessage can set this, "the visitor asked for it" must not become a
   * way to be greeted in a language the brand cannot answer.
   */
  readonly chosen?: string | null;
  /**
   * `data-omni-locale` on the customer's script tag, forwarded through the frame
   * URL. The site knows which language IT is rendering, which is a far better
   * signal than the browser for a visitor reading a Ukrainian page in a Chrome
   * that still lists en-US first.
   */
  readonly requested: string | null;
  /**
   * `navigator.languages`, in the VISITOR's order — see {@link deviceLanguages},
   * which is how this value should be read off a `Navigator`.
   *
   * Typed to admit the absent case even though lib.dom says it cannot happen.
   * The value crosses into this program from a host object, not from our own
   * types, and the cost of being wrong is not a wrong language but an exception
   * thrown inside the widget's first render.
   */
  readonly browser: readonly string[] | null | undefined;
  /**
   * The brand's staffed locales, from widget_config.locales. Empty until the
   * session call has answered — treated as "no constraint yet" rather than as
   * "no language is allowed", so the first paint is not blank.
   */
  readonly supported: readonly string[];
}

/**
 * The shape of the `Navigator` this module actually reads. Structural rather than
 * `Navigator` itself so the function is callable from a test without a DOM, and
 * so the two fields it touches are legible without opening lib.dom.
 */
export interface DeviceLanguageSource {
  readonly languages?: readonly string[] | undefined;
  readonly language?: string | undefined;
}

/**
 * The device's language preferences, in the visitor's own order.
 *
 * `navigator.languages` FIRST because it carries the order — a visitor who reads
 * Ukrainian and then English has said something `navigator.language` cannot
 * express. `language` is the documented fallback for the runtimes that never
 * shipped the plural form, and an empty list is treated as absent rather than as
 * "this visitor prefers nothing", because an empty `languages` is what a
 * stripped-down WebView reports, not a preference.
 *
 * Returns tags UNPARSED. Deciding which of them is a locale is
 * {@link resolveWidgetLocale}'s job, and keeping the two apart is what lets this
 * function be the one place that knows about the host object.
 */
export function deviceLanguages(source: DeviceLanguageSource): readonly string[] {
  // Annotated as `unknown[]`, not inferred: `Array.isArray` on a `readonly
  // string[] | undefined` widens to `any[]`, and an `any` here would let a
  // patched navigator's array of numbers through to `normalizeLocale`.
  const listed: readonly unknown[] = Array.isArray(source.languages) ? source.languages : [];
  const tags = listed.filter((tag): tag is string => typeof tag === 'string' && tag.trim() !== '');
  if (tags.length > 0) {
    return tags;
  }
  const single = source.language;
  return typeof single === 'string' && single.trim() !== '' ? [single] : [];
}

/**
 * One language tag from the outside world, reduced to a locale we have a
 * catalogue for, or `null`.
 *
 * The ONLY repair is the separator. `uk_UA` is the same request as `uk-UA` —
 * BCP-47 says hyphen, several platforms emit underscore, and treating the second
 * as unintelligible costs a visitor their language. Everything else is left to
 * `normalizeLocale`, deliberately: a repair that guessed harder (mapping `ru` to
 * `uk` because they share an alphabet, `sr` to `hr` because a machine can read
 * both) would answer a person in a language they did not ask for, and this file
 * would rather say nothing and let the next signal decide.
 */
function tagLocale(raw: string): WidgetLocale | null {
  const repaired = raw.trim().replaceAll('_', '-');
  return repaired === '' ? null : normalizeWidgetLocale(repaired);
}

/**
 * The brand's staffed locales, reduced to the ones this product has catalogues
 * for. Empty when the brand has said nothing yet, which is not the same as "no
 * language" — see {@link resolveWidgetLocale}.
 *
 * Exported because the language switcher must offer exactly this list. Offering
 * every locale the product ships would let a visitor pick a language the brand
 * cannot answer in, which is a worse outcome than not offering the choice.
 */
export function staffedLocales(supported: readonly string[]): readonly WidgetLocale[] {
  return narrow(supported);
}

function narrow(supported: readonly string[]): readonly WidgetLocale[] {
  const locales: WidgetLocale[] = [];
  for (const tag of supported) {
    // The same repair as the device list, for the same reason: this list is typed
    // into a console field by a human, and `uk_UA` there must not silently unstaff
    // Ukrainian.
    const locale = tagLocale(tag);
    // A brand may list a locale we ship no catalogue for. Dropping it here is
    // deliberate: the alternative is activating a locale with no messages, which
    // renders message ids at a customer.
    if (locale !== null && !locales.includes(locale)) {
      locales.push(locale);
    }
  }
  return locales;
}

/**
 * Resolve the panel's locale. Always returns something — a widget that cannot
 * decide on a language still has to render.
 */
export function resolveWidgetLocale(sources: LocaleSources): WidgetLocale {
  const allowed = narrow(sources.supported);
  const permitted = (locale: WidgetLocale): boolean =>
    allowed.length === 0 || allowed.includes(locale);

  const chosen = sources.chosen ?? null;
  if (chosen !== null) {
    const picked = tagLocale(chosen);
    if (picked !== null && permitted(picked)) {
      return picked;
    }
  }

  if (sources.requested !== null) {
    const requested = tagLocale(sources.requested);
    if (requested !== null && permitted(requested)) {
      return requested;
    }
  }

  // In the visitor's own order, and every entry gets a turn. An entry that says
  // nothing (`*`, `x-default`, a language we ship no catalogue for) must not end
  // the search — the next entry is the visitor's second choice and it beats the
  // brand's default, which is the difference between answering a Ukrainian in
  // Ukrainian and answering them in English because their browser listed `de`
  // first.
  for (const tag of sources.browser ?? []) {
    const locale = tagLocale(tag);
    if (locale !== null && permitted(locale)) {
      return locale;
    }
  }

  // The brand's FIRST staffed locale before the product-wide default: a brand
  // that staffs only English should not greet an unrecognised visitor in
  // Ukrainian just because that is this product's fallback.
  return allowed[0] ?? WIDGET_DEFAULT_LOCALE;
}
